import type { ICandidate } from "@bbj-types/candidate";
import type { ISupportCourse } from "@bbj-types/supportCourse";
import type { IUser } from "@bbj-types/user";
import type { ObjectId, Override } from "@bbj-types/utils";
import type { IWorkshop, IWorkshopDocument, IWorkshopStatus } from "@bbj-types/workshop";
import { useSuspenseWithReload } from "@root/api/tools/axios";
import { useCallback } from "react";
import { supportCourseAxios, supportCourseAxiosInstance } from "./axios";

export type TGetSupportCourse = Override<
  Pick<
    ISupportCourse,
    | "address"
    | "description"
    | "name"
    | "prfe2IdCourse"
    | "isDone"
    | "isCancelled"
    | "internships"
    | "trainingManager"
    | "beginDate"
    | "trainer"
    | "endDate"
    | "_id"
    | "organisation"
  >,
  {
    owner: Pick<IUser, "_id" | "firstName" | "lastName">;
    sessions: (Pick<
      IWorkshop,
      | "_id"
      | "sessionType"
      | "date"
      | "startTime"
      | "endTime"
      | "isDone"
      | "address"
      | "convocationTimes"
      | "description"
      | "organizer"
      | "participants"
    > & {
      mainAnimator?: Pick<IUser, "_id" | "firstName" | "lastName" | "isDisabled">;
      otherAnimators: Pick<IUser, "_id" | "firstName" | "lastName" | "isDisabled">[];
      status: IWorkshopStatus;
      documents: IWorkshopDocument[];
      hasEventBeenSent: boolean;
      lastInvitationDate?: Date;
    })[];
    participants: (Pick<ICandidate, "_id" | "firstName" | "lastName" | "profileId"> & { workshops: ObjectId[] })[];
  }
>;

export const useGetSupportCourseV2 = () => {
  const [state, execute, cancel] = supportCourseAxios<TGetSupportCourse, void, unknown>(
    {
      url: "/supportCourses",
      method: "GET",
      withCredentials: true,
    },
    { manual: true }
  );

  const callback = useCallback(
    (supportCourseId: ObjectId) => {
      return execute({
        url: `/supportCourses/${supportCourseId}`,
      });
    },
    [execute]
  );

  return [state, callback, cancel] as const;
};

export const useGetSupportCourseSuspense = (supportCourseId: ObjectId) => {
  return useSuspenseWithReload(
    () =>
      supportCourseAxiosInstance.get<TGetSupportCourse>(`/supportCourses/${supportCourseId}`, {
        withCredentials: true,
      }),
    [supportCourseId]
  );
};
